import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextBoxComponent from "@CubeComponents/form/TextBoxComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import css from "./Reviews.module.css";
import AddUserModal from "../SpaceSettings/UserAdministration/Workflows/WorkflowReviewLevels/AddUserModal";
import ReviewStageTransitionChart from "./ReviewStageTransitionChart";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import { Autocomplete, Checkbox, TextField, Box } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function ReviewNotificationModal({
  modalTitle = "Send Notification",
  confirmBtnTitle = "Confirm",
  targetLevelReviewers = [],
  targetLevelSerial,
  spaceId,
  cancelCallback,
  confirmCallback,
  approvalStages = [],
  action,
  requireMandatoryComment = false,
  skipReviewTillLevel,
  rollbackReviewToLevel,
  isLoadingApprove,
  setIsLoadingApprove,
}) {
  const { t } = useTranslation();
  const initialTargetLevelReviewerIds = targetLevelReviewers.map((record) => record.userId);

  const defaultReviewNotificationData = {
    selectedReviewersAtTargetLevel: initialTargetLevelReviewerIds,
    selectedReviewersFromSpace: [],
    message: "",
  };

  const [modalData, setModalData] = useState(defaultReviewNotificationData);
  const [spaceUsersModal, setSpaceUsersModal] = useState({ show: false });
  const [selectedReviewers, setSelectedReviewers] = useState(targetLevelReviewers);
  const notificationMultiSelectRef = useRef({ value: [] });

  const messageOnChange = (e) => {
    setModalData((prev) => ({ ...prev, message: e.value }));
  };

  const closeAddUserModal = () => {
    setSpaceUsersModal({ show: false });
  };

  const openUsersInSpaceList = () => {
    setSpaceUsersModal({ show: true });
  };

  const addUserCallback = (selectedUsers) => {
    const selectedUsersMapper = selectedUsers.map(({ userId, firstName, lastName, profilePic }) => ({
      userId,
      firstName,
      lastName,
      profilePic,
    }));

    const currentOtherSpaceUsers = modalData.selectedReviewersFromSpace;
    const currentOtherSpaceUserIds = currentOtherSpaceUsers.map((user) => user.userId);
    const currentLevel1UsersIds = selectedReviewers.map((r) => r.userId);

    const totalCurrentUserIds = [...currentLevel1UsersIds, ...currentOtherSpaceUserIds];

    const newUsersToAdd = selectedUsersMapper.filter((user) => !totalCurrentUserIds.includes(user.userId));
    const totalUsersInOtherSpaceList = [...currentOtherSpaceUsers, ...newUsersToAdd];

    setModalData((prev) => ({
      ...prev,
      selectedReviewersFromSpace: totalUsersInOtherSpaceList,
    }));
    closeAddUserModal();
  };

  const removeReviewerFromLevelNotificationList = (userId) => {
    const selectedReviewersAtTargetLevel = notificationMultiSelectRef.current.value.filter(
      (reviewerId) => reviewerId !== userId,
    );
    notificationMultiSelectRef.current.value = selectedReviewersAtTargetLevel;
    const updated = selectedReviewers.filter((u) => u.userId !== userId);
    setSelectedReviewers(updated);

    setModalData((prev) => ({
      ...prev,
      selectedReviewersAtTargetLevel: updated.map((u) => u.userId),
    }));
  };

  const removeReviewerFromSpaceNotificationList = (userId) => {
    setModalData((prev) => ({
      ...prev,
      selectedReviewersFromSpace: prev.selectedReviewersFromSpace.filter((user) => user.userId !== userId),
    }));
  };

  const confirmNotificationHandler = () => {
    if (typeof setIsLoadingApprove === "function") {
      setIsLoadingApprove(true);
    }

    const { message, selectedReviewersFromSpace } = modalData;

    const selectedReviewerIdsAtTargetLevel = selectedReviewers.map((r) => r.userId);
    const selectedReviewerIdsFromSpace = selectedReviewersFromSpace.map((user) => user.userId);

    confirmCallback({
      message,
      selectedReviewerIdsFromSpace,
      selectedReviewerIdsAtTargetLevel,
    });
  };

  useEffect(() => {
    if (targetLevelSerial && targetLevelReviewers?.length) {
      notificationMultiSelectRef.current.value = initialTargetLevelReviewerIds;
    }
  }, []);

  const getDefaultUserNameComponent = (user) => {
    return (
      <UserNameTagComponent
        id={user.userId}
        handleDeleteIconClicked={() => removeReviewerFromSpaceNotificationList(user.userId)}
      />
    );
  };

  const handleSelect = (event, newValue) => {
    const updatedSelected = newValue.filter((item) => typeof item === "object" && item.userId);

    setSelectedReviewers(updatedSelected);

    setModalData((prev) => ({
      ...prev,
      selectedReviewersAtTargetLevel: updatedSelected.map((u) => u.userId),
      selectedReviewersFromSpace: prev.selectedReviewersFromSpace?.filter(
        (user) => !updatedSelected.some((sel) => sel.userId === user.userId),
      ),
    }));
  };

  const notificationHeader = () => <p className={css.modalTitle}>{modalTitle}</p>;

  const getReviewerMultiSelect = () => (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={targetLevelReviewers}
      value={selectedReviewers}
      onChange={handleSelect}
      disablePortal
      getOptionLabel={(option) => `${option.firstName ?? ""} ${option.lastName ?? ""}`.trim()}
      isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
      renderOption={(props, option, { selected }) => (
        <li {...props} id={`itemTemplate_user_${option.userId}`}>
          <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginInlineEnd: 8 }} checked={selected} />
          <UserNameTagComponent id={option.userId} />
        </li>
      )}
      renderTags={(value) =>
        value.map((option) => (
          <Box key={option.userId} className={css.multiSelectValueTemplate} id={`valueTemplate_user_${option.userId}`}>
            <UserNameTagComponent
              id={option.userId}
              handleDeleteIconClicked={() => removeReviewerFromLevelNotificationList(option.userId)}
            />
          </Box>
        ))
      }
      renderInput={(params) => (
        <TextField {...params} variant="outlined" label="Reviewers" placeholder={t("Choose...")} />
      )}
    />
  );

  const [disabledIds, setDisabledIds] = useState([]);

  useEffect(() => {
    setDisabledIds([
      ...modalData.selectedReviewersFromSpace.map((user) => user.userId),
      ...modalData.selectedReviewersAtTargetLevel,
    ]);
  }, [modalData]);

  const notificationContent = () => {
    const isRollback = action === "rollbackReview";

    return (
      <div className={css.notificationContent}>
        <div className="pt-8 pb-8 ps-8 pe-4 border-b-2 border-system_colour_44 mx-auto">
          {approvalStages.length ? (
            <ReviewStageTransitionChart
              approvalStages={approvalStages}
              isRollback={isRollback}
              skipReviewTillLevel={skipReviewTillLevel}
              rollbackReviewToLevel={rollbackReviewToLevel}
            />
          ) : undefined}
        </div>
        {targetLevelSerial && (
          <div className={css.notificationContentSection} id="reviewsAutoNotificationWrapper">
            <p>Notify Reviewers at Level {targetLevelSerial}</p>
            {getReviewerMultiSelect()}
            <span>
              E-mail Notifications are only sent to reviewers listed above. Any reviewer for this task can take action
              during the review task.
            </span>
          </div>
        )}

        <div className={css.notificationContentSection}>
          <p>{t("Notify Other Members of the Space")}</p>
          <div>
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={openUsersInSpaceList}>
              + Add
            </ButtonComponent>
          </div>
          <div className={css.notificationUsersContainer}>
            {modalData.selectedReviewersFromSpace.map((user) => getDefaultUserNameComponent(user))}
          </div>
          <span>{t("Notified space members can read review comments.")}</span>
        </div>

        <div className={css.notificationContentSection}>
          {requireMandatoryComment ? <p>Custom Message*</p> : <p>{t("Custom Message")}</p>}
          <TextBoxComponent
            multiline
            rows={5}
            value={modalData.message}
            placeholder={t("Enter notification message")}
            onChange={messageOnChange}
          />
        </div>
      </div>
    );
  };

  const notificationFooter = () => (
    <div className={css.notificationFooter}>
      <ButtonComponent
        onClick={confirmNotificationHandler}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        disabled={requireMandatoryComment && !modalData.message.trim()}
        loading={isLoadingApprove}
      >
        {confirmBtnTitle}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={cancelCallback}>
        {t("Cancel")}
      </ButtonComponent>
    </div>
  );

  return (
    <>
      <ModalStateDialogComponent
        isVisible
        allowModalPadding={false}
        width="1000px"
        zIndex={30000}
        headerTitle={notificationHeader()}
        onCancel={cancelCallback}
        modelContent={notificationContent()}
        footerContent={notificationFooter()}
      />

      {spaceUsersModal.show && (
        <AddUserModal
          spaceId={spaceId}
          addUserCallback={addUserCallback}
          closeModal={closeAddUserModal}
          disabledUserIds={disabledIds}
        />
      )}
    </>
  );
}

export default ReviewNotificationModal;
